<script lang="ts">
  import DieElement from '../dice-tray/die.svelte';
  import DieIcon from '../dice-tray/die-icon.svelte';
  import { selectDieData } from './modals';
  import Frame from '../components/frame.svelte';
  import Chrome from '../components/chrome.svelte';
  import type { Die } from '../models/die.type';

  let onConfirm: Function | null = null;
  // raw: a $state proxy of the die would never be === the die offered.
  let die: Die | null = $state.raw(null);

  selectDieData.subscribe((value) => {
    if (value && value.callback) {
      // A new request starts with nothing chosen. chooseDie() re-sets this
      // store with the same callback, and that must not undo the choice.
      if (value.callback !== onConfirm) {
        die = null;
      }
      onConfirm = value.callback;
    } else {
      onConfirm = null;
    }
  });

  const confirm = () => {
    // Nothing chosen yet: there is no die to hand back.
    if (!die) {
      return;
    }
    if (onConfirm) {
      onConfirm(die);
    }
    selectDieData.set(null);
  };

  // Only remembers the choice; the card's ring shows it. This used to set
  // `selected` on the dice to highlight them, clearing it on every die offered
  // -- but `selected` is game state technique validation reads.
  function chooseDie(selectedDie: Die) {
    if (selectedDie.used) {
      return;
    }
    die = selectedDie;
  }
</script>

{#if $selectDieData}
  <div class="modal-container">
    <div class="modal">
      <Frame>
        <div class="header">
          <h1>{$selectDieData.title ?? 'Select a Die'}</h1>
          {#if $selectDieData.step}
            <p class="step">{$selectDieData.step}</p>
          {/if}
        </div>

        <div class="body">
          {#if $selectDieData.text}
            <p class="explain">{$selectDieData.text}</p>
          {/if}

          <!-- Whole dice, not faces: each card is the die as it shows now, its
               name, and every face it has -- so choosing one reads as choosing
               a die, and the face picker that may follow looks different. -->
          <div class="dice">
            {#each $selectDieData.dice as option (option.id)}
              <button
                class="die-option {option === die ? 'chosen' : ''}"
                disabled={option.used}
                onclick={() => chooseDie(option)}
              >
                <DieElement die={option} die3D={false} hover={false} redraw={$selectDieData.dice} />
                <span class="name">{option.type}</span>
                <span class="faces">
                  {#each option.faces as face, index}
                    <span class="face-chip {index == option.activeFace ? 'showing' : ''}">
                      <DieIcon {face} />
                    </span>
                  {/each}
                </span>
              </button>
            {/each}
          </div>
        </div>

        <div class="functions {die ? '' : 'waiting'}">
          <Chrome chroming="gold" edge="circle" thickness="thin" click={confirm}>
            <div class="button">Confirm</div>
          </Chrome>
        </div>
      </Frame>
    </div>
  </div>
{/if}

<style lang="less">
  .step {
    margin: 0.2em 0 0;
    opacity: 0.8;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .explain {
    margin: 0 0 12px;
    text-align: center;
    opacity: 0.9;
  }

  .dice {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
  }

  // Over the global button look, which would put a bevel round every card.
  .die-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 10px 10px 8px;
    background: rgba(0, 0, 0, 0.35);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2);
    border-radius: 12px;

    &.chosen {
      box-shadow:
        0 0 0 2px rgba(255, 255, 255, 0.85),
        0 0 12px rgba(255, 255, 255, 0.55);
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    .name {
      color: white;
      text-transform: capitalize;
      text-shadow: 0 1px 3px black;
    }
  }

  // The die's six faces, the one showing now marked.
  .faces {
    display: grid;
    grid-template-columns: repeat(3, 22px);
    gap: 3px;
  }

  .face-chip {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1px;
    width: 22px;
    height: 22px;
    padding: 3px;
    box-sizing: border-box;
    overflow: hidden;

    // DieIcon sizes itself through global .icon-100 and friends, sized for a
    // full die; a chip needs its icons a fraction of that.
    :global(.icon) {
      --icon-size: 14px;
      width: 14px;
      height: 14px;
      flex-shrink: 1;
      min-width: 0;
    }
    :global(.vr) {
      display: none;
    }
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.12);
    opacity: 0.6;

    &.showing {
      opacity: 1;
      box-shadow: 0 0 0 1.5px white;
    }
  }

  // Confirm does nothing until a die is chosen, so it says so.
  .functions.waiting {
    opacity: 0.5;
    pointer-events: none;
  }
</style>
